import type { StageEventSnapshot } from "@/api/client";
import {
  AUTO_LABEL,
  eventSourceLabel,
  STAGE_LABEL,
  STATUS_LABEL,
} from "@/lib/candidateStages";
import type { RealtimeEvent } from "@/lib/realtime";

// Всплывашки «сменился этап у кандидата в моём чате» (StageToastHost) —
// чистая логика без React и таймеров, чтобы проверять её тестами.
//
// Кому: владельцу чата (`owner_user_id` события — Chat.recruiter_id), и
// только когда сменил не он сам (`actor_user_id`; null — автоматика, её
// показываем). SSE `stage` несёт одни id (поток видят все вкладки всех
// рекрутёров) — текст приходит снимком GET /chat/stage-events/{id}.
//
// Пачки. Массовый отказ и скрининг меняют десятки кандидатур разом, а
// chat-service публикует по событию на строку. Хост копит события
// COALESCE_MS; от SUMMARY_FROM разных кандидатур в пачке — одна сводная
// «Изменён этап у N кандидатов» (без запросов), меньше — по всплывашке на
// кандидатуру (её последняя смена). Пока сводная на экране, новое к ней
// прибавляется, а не рисуется рядом: пачка, разрезанная по циклам разбора
// журнала (chat-service пишет строки порциями), остаётся одной сводной.

/** Сколько ждать хвоста пачки событий. */
export const COALESCE_MS = 1200;
/** Столько разных кандидатур в пачке — уже сводная. */
export const SUMMARY_FROM = 3;
/** Больше всплывашек разом не держим: старые уходят. */
export const MAX_VISIBLE = 3;
/** Сколько висит всплывашка (пока её не трогают мышью или фокусом). */
export const HIDE_MS = 8000;

export interface StageSignal {
  eventId: string;
  candidateId: string | null;
  personId: string | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** Событие потока → сигнал для моей всплывашки, или null: не `stage`, не мой
 *  чат, сменил я сам, или ещё не вошли. */
export function stageSignal(ev: RealtimeEvent, me: string | null): StageSignal | null {
  if (ev.kind !== "stage" || !me) return null;
  if (ev.owner_user_id !== me || ev.actor_user_id === me) return null;
  const eventId = str(ev.stage_event_id);
  if (!eventId) return null;
  return { eventId, candidateId: str(ev.candidate_id), personId: str(ev.person_id) };
}

export interface BurstPlan {
  /** Все события пачки — запомнить как показанные. */
  ids: string[];
  /** Кандидатуры для сводной; пусто — сводной нет. */
  summary: string[];
  /** События, по которым запросить снимок и показать всплывашку. */
  fetch: string[];
}

/** Что делать с накопленной пачкой. Уже показанные события (`seen`) — мимо:
 *  одно и то же событие дважды (перемонтирование, повтор публикации) не
 *  всплывает дважды. `summaryShown` — сводная уже на экране: всё в неё, без
 *  запросов (хвост той же массовой смены, дописанный следующим циклом). */
export function planBurst(
  signals: readonly StageSignal[],
  seen: ReadonlySet<string>,
  summaryShown = false,
): BurstPlan | null {
  const fresh = new Map<string, StageSignal>();
  for (const s of signals) if (!seen.has(s.eventId)) fresh.set(s.eventId, s);
  if (!fresh.size) return null;
  // Одна кандидатура — одна всплывашка: этап и статус её за пачку — последняя.
  const latest = new Map<string, StageSignal>();
  for (const s of fresh.values()) latest.set(s.candidateId ?? s.eventId, s);
  const ids = [...fresh.keys()];
  if (summaryShown || latest.size >= SUMMARY_FROM) return { ids, summary: [...latest.keys()], fetch: [] };
  return { ids, summary: [], fetch: [...latest.values()].map((s) => s.eventId) };
}

// ── что на экране ──

export interface OneToast {
  kind: "one";
  key: string;
  candidateId: string;
  personId: string | null;
  /** Сменился только статус (закрыта / снова в работе), не этап. */
  statusOnly: boolean;
  /** «Этап кандидата» / «Статус кандидата» — надпись над заголовком. */
  eyebrow: string;
  /** «Повар · Иван Петров». */
  title: string;
  /** «Встреча назначена → Встреча проведена», «Кандидат отказался». */
  change: string;
  /** «автоматически: по итогам встречи» / «Анна Коваль». */
  by: string;
  /** Сменила Дева (автоматика) — у всплывашки её аватарка вместо значка. */
  deva: boolean;
  /** Всё одной строкой — для дикторов. */
  aria: string;
}

export interface SummaryToast {
  kind: "summary";
  key: string;
  /** Чьи смены вошли (кандидатуры) — число в заголовке; одна кандидатура
   *  дважды не считается. */
  candidates: readonly string[];
  /** Растёт при каждом прибавлении — таймер скрытия заводится заново. */
  rev: number;
}

export type StageToast = OneToast | SummaryToast;

/** «у 1 кандидата», «у 3 кандидатов», «у 21 кандидата», «у 11 кандидатов». */
export function candidatesGen(n: number): string {
  const one = n % 10 === 1 && n % 100 !== 11;
  return `${n} ${one ? "кандидата" : "кандидатов"}`;
}

export const summaryTitle = (n: number): string => `Изменён этап у ${candidatesGen(n)}`;

/** Что сменилось, словами — как строка ленты (снимок — по всей её склейке).
 *  Статус: «Отказ — причина», «Снова в работе»; этап — «откуда → куда»; этап и
 *  статус одним действием — через запятую: «Встреча назначена → Встреча
 *  проведена, отказ — причина». Подписи — снимка, а нет их — свои
 *  (lib/candidateStages). */
export function changeText(
  e: Pick<
    StageEventSnapshot,
    "kind" | "from" | "to" | "from_label" | "to_label" | "reason" | "status_to" | "status_to_label"
  >,
): string {
  const reason = e.reason?.trim();
  const status = (to: string, label: string | null | undefined) => {
    if (to === "active") return "Снова в работе";
    const text = label?.trim() || STATUS_LABEL[to] || to;
    return reason ? `${text} — ${reason}` : text;
  };
  if (e.kind === "status") return status(e.to, e.to_label);
  const to = e.to_label?.trim() || STAGE_LABEL[e.to] || e.to;
  const from = e.from ? e.from_label?.trim() || STAGE_LABEL[e.from] || e.from : null;
  const stage = from ? `${from} → ${to}` : to;
  if (!e.status_to) return stage;
  const st = status(e.status_to, e.status_to_label);
  return `${stage}, ${st[0].toLowerCase()}${st.slice(1)}`;
}

/** Кто сменил — как в строке ленты: человек — его имя, а сделал он не просто
 *  «вручную» — «Анна Коваль: запись на встречу»; без человека —
 *  «автоматически: откуда». */
export function byText(
  e: Pick<StageEventSnapshot, "auto" | "actor_name" | "source" | "source_label">,
): string {
  if (!e.auto) {
    const name = e.actor_name?.trim();
    const did = e.source_label?.trim();
    if (!name) return "вручную";
    return did && did !== "вручную" ? `${name}: ${did}` : name;
  }
  const src = e.source_label?.trim() || eventSourceLabel(e.source);
  return src === AUTO_LABEL ? AUTO_LABEL : `${AUTO_LABEL}: ${src}`;
}

export function stageToastView(e: StageEventSnapshot): OneToast {
  const who = e.person_name?.trim() || "Без имени";
  const title = e.vacancy_title?.trim() ? `${e.vacancy_title.trim()} · ${who}` : who;
  const change = changeText(e);
  const by = byText(e);
  return {
    kind: "one",
    key: e.id,
    candidateId: e.candidate_id,
    personId: e.person_id,
    statusOnly: e.kind === "status",
    eyebrow: e.kind === "status" ? "Статус кандидата" : "Этап кандидата",
    title,
    change,
    by,
    deva: e.actor === "deva",
    aria: `${title}: ${change} · ${by}`,
  };
}

/** Прибавить кандидатуры к сводной: есть на экране — к ней, нет — новая.
 *  Всплывашки о тех же кандидатурах сводная заменяет. */
export function addSummary(
  items: readonly StageToast[],
  candidates: readonly string[],
  key: string,
): StageToast[] {
  const rest = items.filter((t) => t.kind !== "one" || !candidates.includes(t.candidateId));
  const i = rest.findIndex((t) => t.kind === "summary");
  if (i < 0) return capped([...rest, { kind: "summary", key, candidates: [...new Set(candidates)], rev: 0 }]);
  const cur = rest[i] as SummaryToast;
  rest[i] = { ...cur, candidates: [...new Set([...cur.candidates, ...candidates])], rev: cur.rev + 1 };
  return rest;
}

/** Показать одну. Сводная на экране — прибавить к ней. Та же кандидатура уже
 *  на экране — заменить: новая смена важнее прежней. */
export function addOne(items: readonly StageToast[], toast: OneToast): StageToast[] {
  if (items.some((t) => t.key === toast.key)) return [...items];
  if (items.some((t) => t.kind === "summary")) return addSummary(items, [toast.candidateId], toast.key);
  const rest = items.filter((t) => t.kind !== "one" || t.candidateId !== toast.candidateId);
  return capped([...rest, toast]);
}

export function removeToast(items: readonly StageToast[], key: string): StageToast[] {
  return items.filter((t) => t.key !== key);
}

function capped(items: StageToast[]): StageToast[] {
  return items.length > MAX_VISIBLE ? items.slice(items.length - MAX_VISIBLE) : items;
}
